import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { StorageApi } from '@/lib/api';

interface UploadParams {
  file: File;
  key: string;
}

/**
 * Upload mutation for a storage arche.
 * Gets a presigned URL then uploads directly to S3.
 * Invalidates all queries for the given namespace on success.
 */
export function useStorageUpload(api: StorageApi, queryKeyAll: readonly string[]) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ file, key }: UploadParams) => {
      // Get presigned URL
      const { uploadUrl, cdnUrl } = await api.getUploadUrl(key, file.type, file.size);

      // Upload directly to S3
      const response = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      });

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.status}`);
      }

      return { key, cdnUrl };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...queryKeyAll] });
    },
  });
}
